import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Box,
  Button,
  HStack,
  Select,
  Spinner,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { Stack as StackIcon, Plus, MagicWand, MagnifyingGlass } from 'phosphor-react';
import { toast } from 'sonner';
import moment from 'moment';
import { PageBox } from '../../../../../../components/pageBox';
import { ReturnButton } from '../../../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../../../hooks/useMonthFilter';
import { useIcms } from '../../../../../../hooks/useIcms';
import { AcumuladoresTable } from '../components/AcumuladoresTable';
import { AcumuladorFormModal } from '../components/AcumuladorFormModal';
import { AcumuladoresSugeridosDrawer } from '../components/AcumuladoresSugeridosDrawer';
import { IAcumulador } from '../../../../../../types/icms';

export function IcmsAcumuladoresEmpresaPage() {
  const { codiEmp } = useParams<{ codiEmp: string }>();
  const codiEmpNum = Number(codiEmp);
  const queryClient = useQueryClient();
  const { getAcumuladores, deleteAcumulador } = useIcms();

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({ defaultToPreviousMonth: true });

  const [searchParams, setSearchParams] = useSearchParams();
  const tipoOp = searchParams.get('tipo_operacao') || '';
  const classif = searchParams.get('classificacao') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '50', 10);

  const formModal = useDisclosure();
  const sugeridosDrawer = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState<IAcumulador | null>(null);
  const [deleting, setDeleting] = useState<IAcumulador | null>(null);

  const filters = useMemo(
    () => ({
      codi_emp: codiEmpNum,
      ...(tipoOp ? { tipo_operacao: tipoOp } : {}),
      ...(classif ? { classificacao: classif } : {}),
      ...(search ? { search } : {}),
      page,
      size,
    }),
    [codiEmpNum, tipoOp, classif, search, page, size],
  );

  const tableQuery = useQuery({
    queryKey: ['acumuladores', filters],
    queryFn: getAcumuladores,
    enabled: !!codiEmpNum,
    placeholderData: prev => prev,
  });

  const items = tableQuery.data?.data?.items ?? [];
  const pagination = {
    total: tableQuery.data?.data?.total ?? 0,
    pages: tableQuery.data?.data?.pages ?? 0,
    page: tableQuery.data?.data?.page ?? 1,
    size: tableQuery.data?.data?.size ?? size,
    next_page: tableQuery.data?.data?.next_page ?? null,
    previous_page: tableQuery.data?.data?.previous_page ?? null,
  };

  const setQueryParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const deleteMutation = useMutation({
    mutationFn: async (row: IAcumulador) => deleteAcumulador(codiEmpNum, row.codi_acu),
    onSuccess: () => {
      toast.success('Acumulador removido');
      queryClient.invalidateQueries({ queryKey: ['acumuladores'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao remover. Tente novamente.');
    },
  });

  return (
    <PageBox>
      <Flex align="center" pb={5}>
        <ReturnButton previousPage="/modulesHome/supervisor/icms/acumuladores" />
        <Box h="full">
          <Flex align="center" justify="center" borderRadius="full" p={3} bg="#F3E5F5">
            <StackIcon color="#9C27B0" className="size-8" />
          </Flex>
        </Box>
        <Flex direction="column" justify="center" gap={1} pl={2}>
          <Text fontWeight="bold" fontSize="16px">
            Acumuladores · Empresa #{codiEmpNum}
          </Text>
          <Text fontSize="12px" color="gray.500">
            Período base: {moment(dateStart).format('DD/MM/YYYY')} a{' '}
            {moment(dateEnd).format('DD/MM/YYYY')}
          </Text>
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <MonthNavigator
          currentDate={currentDate}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onSelectMonth={handleSelectMonth}
        />
        <HStack>
          <Button
            size="sm"
            colorScheme="purple"
            leftIcon={<MagicWand size={16} />}
            onClick={sugeridosDrawer.onOpen}
          >
            Descobrir acumuladores
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<Plus size={16} />}
            onClick={() => {
              setEditing(null);
              formModal.onOpen();
            }}
          >
            Novo
          </Button>
        </HStack>
      </Flex>

      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <InputGroup size="sm" maxW="280px">
          <InputLeftElement pointerEvents="none">
            <MagnifyingGlass size={14} color="#94a3b8" />
          </InputLeftElement>
          <Input
            placeholder="Buscar código ou nome..."
            value={search}
            background="body"
            onChange={e => setQueryParam('search', e.target.value)}
          />
        </InputGroup>
        <Select
          size="sm"
          maxW="160px"
          background="body"
          value={tipoOp}
          onChange={e => setQueryParam('tipo_operacao', e.target.value)}
        >
          <option value="">Todas operações</option>
          <option value="entrada">Entrada</option>
          <option value="saida">Saída</option>
        </Select>
        <Select
          size="sm"
          maxW="180px"
          background="body"
          value={classif}
          onChange={e => setQueryParam('classificacao', e.target.value)}
        >
          <option value="">Todas classificações</option>
          <option value="difal">DIFAL</option>
          <option value="st">ST</option>
          <option value="simples">Simples</option>
          <option value="isenta">Isenta</option>
          <option value="nao_tributada">Não tributada</option>
          <option value="credito_outros">Crédito outros</option>
          <option value="servico">Serviço</option>
          <option value="outras">Outras</option>
        </Select>
        {tableQuery.isFetching && <Spinner size="sm" color="primary" />}
      </Flex>

      <AcumuladoresTable
        data={items}
        pagination={pagination}
        isFetching={tableQuery.isFetching}
        onEdit={row => {
          setEditing(row);
          formModal.onOpen();
        }}
        onDelete={row => {
          setDeleting(row);
          deleteDialog.onOpen();
        }}
      />

      {formModal.isOpen && (
        <AcumuladorFormModal
          isOpen={formModal.isOpen}
          onClose={formModal.onClose}
          codiEmp={codiEmpNum}
          acumulador={editing}
        />
      )}

      {sugeridosDrawer.isOpen && (
        <AcumuladoresSugeridosDrawer
          isOpen={sugeridosDrawer.isOpen}
          onClose={sugeridosDrawer.onClose}
          codiEmp={codiEmpNum}
          dtInicio={dateStart}
          dtFim={dateEnd}
        />
      )}

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover acumulador
            </AlertDialogHeader>
            <AlertDialogBody>
              Confirmar remoção do acumulador <strong>{deleting?.codi_acu}</strong> —{' '}
              {deleting?.nome_acu}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={deleteMutation.isPending}
                onClick={() => {
                  if (deleting) {
                    deleteMutation.mutate(deleting, {
                      onSuccess: () => deleteDialog.onClose(),
                    });
                  }
                }}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageBox>
  );
}
